function webhookHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

export interface WebhookCallItem {
  id: number;
  type: 'COMMAND' | 'ROUTINE' | string;
  status: 'RUNNING' | 'DONE' | 'FAILED' | string;
  title: string | null;
  taskNumber: string | null;
  createdAt: string;
}

export interface WebhookAnalyticsSummary {
  totalCount: number;
  commandsCreated: number;
  routinesTriggered: number;
  successRate: number;
}

export interface WebhookAnalyticsReport {
  summary: WebhookAnalyticsSummary;
  all: WebhookCallItem[];
  commands: WebhookCallItem[];
  routines: WebhookCallItem[];
}

export type WebhookCallType = 'all' | 'command' | 'routine';

export interface WebhookCallDetailItem extends WebhookCallItem {
  taskId: number | null;
  taskTransactionId: string | null;
  routineId: string | null;
  routineRunId: number | null;
  errorMessage: string | null;
  /** The linked Command/Routine still exists and can be opened. */
  targetAvailable: boolean;
  /** A Command/Routine was linked but has since been deleted. */
  targetDeleted: boolean;
}

export interface WebhookCallsPage {
  items: WebhookCallDetailItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  counts: { all: number; commands: number; routines: number };
}

export interface WebhookCallsParams {
  type: WebhookCallType;
  /** ISO timestamp, inclusive. */
  fromDate?: string;
  /** ISO timestamp, exclusive (start of the day after the picked end date). */
  toDate?: string;
  search?: string;
  page: number;
  pageSize: number;
}

// Backs /settings/webhooks/report — server-paged call list for one tab plus all three
// tab counts under the same date/search filters.
export async function getWebhookCalls(params: WebhookCallsParams, signal?: AbortSignal): Promise<WebhookCallsPage> {
  const qs = new URLSearchParams({
    type: params.type,
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.fromDate) qs.set('fromDate', params.fromDate);
  if (params.toDate) qs.set('toDate', params.toDate);
  if (params.search?.trim()) qs.set('search', params.search.trim());
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/WebhookAnalytics/calls?${qs}`, {
    headers: webhookHeaders(),
    signal,
  });
  if (!res.ok) throw new Error('Failed to load webhook calls');
  return res.json();
}

// Backs the /settings/webhooks right-sidebar's "Webhook Analytics" panel — one call
// returns the 4 summary tiles plus the recent-5 lists for all three tabs, since the
// panel switches tabs client-side without refetching.
export async function getWebhookAnalyticsReport(): Promise<WebhookAnalyticsReport> {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/WebhookAnalytics/summary`, {
    headers: webhookHeaders(),
  });
  if (!res.ok) throw new Error('Failed to load webhook analytics');
  return res.json();
}

/** GET /api/WebhookAnalytics/calls/{id} — one call for the report's detail sidebar. */
export interface WebhookCallFullDetail extends WebhookCallDetailItem {
  modifiedAt: string | null;
  /** Stored path, e.g. /api/insurance-agency/webhooks/command. */
  endpoint: string | null;
  /** Full URL the caller posted to. */
  url: string | null;
  method: string;
  /** Raw JSON text (request headers aren't stored for webhook calls). */
  requestBody: string | null;
  responseBody: string | null;
  routineName: string | null;
}

export async function getWebhookCallDetail(id: number, signal?: AbortSignal): Promise<WebhookCallFullDetail> {
  const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/WebhookAnalytics/calls/${id}`, {
    headers: webhookHeaders(),
    signal,
  });
  if (res.status === 404) throw new Error('This webhook call could not be found.');
  if (!res.ok) throw new Error('Could not load this webhook call. Please try again.');
  return res.json();
}
